import { useState, useEffect } from "react";
import axios from "axios";
import "./Support.css";
import {
  base_url,
  webServiceUrl,
} from "../../../components/apiCall/webserviceurl";

const api = axios.create();

// Attach the token from localStorage to every request automatically.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

const ROLES = [
  { id: "participant", label: "Participant" },
  { id: "lead", label: "LEAD" },
  { id: "admin", label: "ADMIN" },
];

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

function getFileExt(url) {
  if (!url) return "";

  const clean = url.split("?")[0];
  const parts = clean.split(".");

  return parts.length > 1
    ? parts[parts.length - 1].toLowerCase()
    : "";
}

function getFileName(url) {
  if (!url) return "";

  const clean = url.split("?")[0];

  return clean.substring(clean.lastIndexOf("/") + 1);
}

function isImageUrl(url) {
  return IMAGE_EXTENSIONS.includes(getFileExt(url));
}

// Maps one raw API post object -> the shape the UI below uses.
function mapPostToThread(post) {
  let parsedComments = [];

  if (post.comments) {
    try {
      const raw =
        typeof post.comments === "string"
          ? JSON.parse(post.comments)
          : post.comments;

      parsedComments = (raw || []).map((c) => ({
        id: c.comment_id,
        author: c.commented_sender_name,
        role: "participant",
        text: c.comment,
        at: c.commented_date,
        likes: Number(c.comment_likes) || 0,
        selfLiked: !!Number(c.self_liked),
        isComment: true,
      }));
    } catch (err) {
      console.error(
        "Failed to parse comments for post",
        post.post_id,
        err
      );
    }
  }

  return {
    id: post.post_id,
    title: post.post_description,
    description: post.post_description,
    raisedBy: post.created_sender_name,
    status: post.status || "open",
    createdAt: post.post_date || post.created_date,
    image: post.image_url || null,
    likes: post.likes || 0,
    selfLiked: !!post.self_liked,
    commentCount: post.comment_count || 0,

    messages: [
      {
        id: null,
        author: post.created_sender_name,
        role: "participant",
        text: post.post_description,
        at: post.post_date || post.created_date,
        isComment: false,
      },
      ...parsedComments,
    ],
  };
}

export default function Support() {
  const [currentRole, setCurrentRole] = useState("participant");
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [showNewIssue, setShowNewIssue] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [submitting, setSubmitting] = useState(false);

  const activeThread =
    threads.find((t) => t.id === activeId) || null;

  const isLead = currentRole === "lead";
  const isAdmin = currentRole === "admin";
  const isParticipant = currentRole === "participant";

  const visibleThreads = threads.filter(
    (t) => statusFilter === "all" || t.status === statusFilter
  );

  const openCount = threads.filter(
    (t) => t.status === "open"
  ).length;

  // Fetch threads on mount
  useEffect(() => {
    fetchThreads();
  }, []);

  async function fetchThreads() {
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await api.get(
        webServiceUrl.get_community_threads
      );

      const rawPosts = res.data?.data || [];

      setThreads(rawPosts.map(mapPostToThread));
    } catch (err) {
      console.error("Failed to fetch threads:", err);
      setErrorMsg(
        "Failed to load threads. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  // Create a new thread
  async function raiseIssue(e) {
    e.preventDefault();

    if (!newTitle.trim() || !newDesc.trim()) return;

    setSubmitting(true);
    setErrorMsg("");

    try {
      const formData = new FormData();

      // formData.append("title", newTitle.trim());

      formData.append(
        "post_description",
        newDesc.trim()
      );

      if (newImage) {
        formData.append("post_img", newImage);
      }

      const res = await api.post(
        webServiceUrl.post_community_thread,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const createdRaw =
        res.data?.data ||
        res.data?.post ||
        res.data;

      const created = mapPostToThread(createdRaw);

      setThreads((prev) => [created, ...prev]);

      setNewTitle("");
      setNewDesc("");
      setNewImage(null);
      setShowNewIssue(false);
      setActiveId(created.id);

      fetchThreads();
    } catch (err) {
      console.error("Failed to raise issue:", err);

      setErrorMsg(
        "Failed to submit issue. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  function toSqlDateTime(date) {
    const pad = (n) => String(n).padStart(2, "0");

    return (
      `${date.getFullYear()}-${pad(
        date.getMonth() + 1
      )}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(
        date.getMinutes()
      )}:${pad(date.getSeconds())}`
    );
  }

  const [sendingReply, setSendingReply] =
    useState(false);

  async function sendReply(e) {
    e.preventDefault();

    if (
      !replyText.trim() ||
      !activeThread ||
      sendingReply
    ) {
      return;
    }

    const commentText = replyText.trim();

    const commentedSenderName = isLead
      ? "LEAD"
      : "You";

    const commentedDate =
      toSqlDateTime(new Date());

    setSendingReply(true);
    setErrorMsg("");

    try {
      const payload = {
        post_id: activeThread.id,
        comment: commentText,
        commented_sender_name:
          commentedSenderName,
        commented_date: commentedDate,
      };

      await api.post(
        webServiceUrl.post_comment,
        payload
      );

      const msg = {
        author: commentedSenderName,
        role: currentRole,
        text: commentText,
        at: commentedDate,
      };

      setThreads((prev) =>
        prev.map((t) =>
          t.id === activeThread.id
            ? {
                ...t,
                messages: [
                  ...t.messages,
                  msg,
                ],
                commentCount:
                  (t.commentCount || 0) + 1,
              }
            : t
        )
      );

      setReplyText("");
    } catch (err) {
      console.error(
        "Failed to post comment:",
        err
      );

      setErrorMsg(
        "Failed to send reply. Please try again."
      );
    } finally {
      setSendingReply(false);
    }
  }

  // Delete a thread/post
  async function deletePost(postId) {
    setErrorMsg("");

    try {
      await api.post(
        webServiceUrl.delete_post,
        {
          post_id: postId,
        }
      );

      setThreads((prev) =>
        prev.filter((t) => t.id !== postId)
      );

      if (activeId === postId) {
        setActiveId(null);
      }
    } catch (err) {
      console.error(
        "Failed to delete post:",
        err
      );

      setErrorMsg(
        "Failed to delete post. Please try again."
      );
    }
  }

  // Like / unlike a post
  async function likePost(postId) {
    setErrorMsg("");

    let previous;

    setThreads((prev) =>
      prev.map((t) => {
        if (t.id !== postId) return t;

        previous = t;

        const nextSelfLiked = !t.selfLiked;

        return {
          ...t,
          selfLiked: nextSelfLiked,
          likes:
            t.likes +
            (nextSelfLiked ? 1 : -1),
        };
      })
    );

    try {
      await api.post(
        webServiceUrl.like_post,
        {
          post_id: postId,
        }
      );
    } catch (err) {
      console.error(
        "Failed to like post:",
        err
      );

      setErrorMsg(
        "Failed to like post. Please try again."
      );

      if (previous) {
        setThreads((prev) =>
          prev.map((t) =>
            t.id === postId
              ? previous
              : t
          )
        );
      }
    }
  }

  // Like / unlike a comment
  async function likeComment(
    postId,
    commentId
  ) {
    if (!commentId) return;

    setErrorMsg("");

    let previous;

    setThreads((prev) =>
      prev.map((t) => {
        if (t.id !== postId) return t;

        previous = t;

        return {
          ...t,

          messages: t.messages.map(
            (m) => {
              if (m.id !== commentId) {
                return m;
              }

              const nextSelfLiked =
                !m.selfLiked;

              return {
                ...m,
                selfLiked:
                  nextSelfLiked,
                likes:
                  (m.likes || 0) +
                  (nextSelfLiked
                    ? 1
                    : -1),
              };
            }
          ),
        };
      })
    );

    try {
      await api.post(
        webServiceUrl.like_comment,
        {
          comment_id: commentId,
        }
      );
    } catch (err) {
      console.error(
        "Failed to like comment:",
        err
      );

      setErrorMsg(
        "Failed to like comment. Please try again."
      );

      if (previous) {
        setThreads((prev) =>
          prev.map((t) =>
            t.id === postId
              ? previous
              : t
          )
        );
      }
    }
  }

  function resolveThread() {
    if (!activeThread || !isLead) return;

    setThreads((prev) =>
      prev.map((t) =>
        t.id === activeThread.id
          ? {
              ...t,
              status: "resolved",
            }
          : t
      )
    );
  }

  function formatDate(iso) {
    if (!iso) return "";

    return new Date(iso).toLocaleString(
      undefined,
      {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  }

  // Render attached image/file
  function renderAttachment(rawUrl) {
    if (!rawUrl) return null;

    // base_url is used directly here.
    // No API_BASE_URL or getFullUrl is required.
    const fullUrl = `${base_url}${rawUrl}`;

    if (isImageUrl(rawUrl)) {
      return (
        <img
          className="sup-thread-image"
          src={fullUrl}
          alt=""
        />
      );
    }

    return (
      <a
        className="sup-file-chip"
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) =>
          e.stopPropagation()
        }
      >
        <span className="sup-file-chip-icon">
          📄
        </span>

        <span className="sup-file-chip-name">
          {getFileName(rawUrl)}
        </span>
      </a>
    );
  }

  return (
    <div className="sup-page">

      {/* Role switcher — remove once wired to real auth */}

      {/* {errorMsg && (
        <p className="sup-empty">
          {errorMsg}
        </p>
      )} */}

      {loading ? (
        <p className="sup-empty">
          Loading threads...
        </p>
      ) : !activeThread ? (
        <>
          {/* Header */}
          <div className="sup-header">
            <div>
              <h1 className="sup-title">
                Support
              </h1>

              {/* <p className="sup-subtitle">
                Issues are raised to LEAD and
                resolved by LEAD only. Threads
                remain visible to ADMIN for
                oversight.
              </p> */}
            </div>

            {isParticipant && (
              <button
                type="button"
                className="sup-primary-btn"
                onClick={() =>
                  setShowNewIssue(true)
                }
              >
                + Raise an issue
              </button>
            )}
          </div>

          <div className="sup-stats-row">
            <div className="sup-stat">
              <span className="sup-stat-num">
                {threads.length}
              </span>

              <span className="sup-stat-label">
                Total threads
              </span>
            </div>

            <div className="sup-stat">
              <span className="sup-stat-num sup-stat-num-open">
                {openCount}
              </span>

              <span className="sup-stat-label">
                Open
              </span>
            </div>

            <div className="sup-stat">
              <span className="sup-stat-num sup-stat-num-resolved">
                {threads.length -
                  openCount}
              </span>

              <span className="sup-stat-label">
                Resolved
              </span>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="sup-filter-row">
            {[
              "all",
              "open",
              "resolved",
            ].map((f) => (
              <button
                key={f}
                type="button"
                className={`sup-filter-tab${
                  statusFilter === f
                    ? " sup-filter-tab-active"
                    : ""
                }`}
                onClick={() =>
                  setStatusFilter(f)
                }
              >
                {f === "all"
                  ? "All"
                  : f === "open"
                  ? "Open"
                  : "Resolved"}
              </button>
            ))}
          </div>

          {/* Thread list */}
          <div className="sup-thread-list">
            {visibleThreads.length ===
              0 && (
              <p className="sup-empty">
                No threads here yet.
              </p>
            )}

            {visibleThreads.map((t) => (
              <div
                key={t.id}
                className="sup-thread-card-wrap"
              >
                <button
                  type="button"
                  className="sup-thread-card"
                  onClick={() =>
                    setActiveId(t.id)
                  }
                >
                  <div className="sup-thread-card-top">
                    <h3>{t.title}</h3>

                    <span
                      className={`sup-status-badge sup-status-${t.status}`}
                    >
                      {t.status === "open"
                        ? "Open"
                        : "Resolved"}
                    </span>
                  </div>

                  <p className="sup-thread-desc">
                    {t.description}
                  </p>

                  {renderAttachment(
                    t.image
                  )}

                  <div className="sup-thread-meta">
                    <span>
                      {t.raisedBy}
                    </span>

                    <span>·</span>

                    <span>
                      {formatDate(
                        t.createdAt
                      )}
                    </span>

                    <span>·</span>

                    <span>
                      {t.messages?.length ||
                        0}{" "}
                      message
                      {t.messages?.length !==
                      1
                        ? "s"
                        : ""}
                    </span>
                  </div>

                  <div className="sup-thread-social-row">
                    <span
                      role="button"
                      tabIndex={0}
                      className={`sup-like-pill${
                        t.selfLiked
                          ? " sup-like-pill-active"
                          : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        likePost(t.id);
                      }}
                    >
                      ❤ {t.likes}
                    </span>

                    <span className="sup-comment-pill">
                      💬{" "}
                      {t.commentCount ||
                        0}
                    </span>
                  </div>
                </button>

                {/* {isLead && ( */}
                <button
                  type="button"
                  className="sup-card-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    deletePost(t.id);
                  }}
                >
                  Delete
                </button>
                {/* )} */}
              </div>
            ))}
          </div>
        </>
      ) : (
        // Thread detail view
        <div className="sup-detail">
          <button
            type="button"
            className="sup-back-btn"
            onClick={() =>
              setActiveId(null)
            }
          >
            ← Back to Support
          </button>

          <div className="sup-detail-head">
            <div>
              <h2>
                {activeThread.title}
              </h2>

              <div className="sup-thread-meta">
                <span>
                  {activeThread.raisedBy}
                </span>

                <span>·</span>

                <span>
                  {formatDate(
                    activeThread.createdAt
                  )}
                </span>
              </div>
            </div>

            <span
              className={`sup-status-badge sup-status-${activeThread.status}`}
            >
              {activeThread.status ===
              "open"
                ? "Open"
                : "Resolved"}
            </span>
          </div>

          {renderAttachment(
            activeThread.image
          )}

          <div className="sup-thread-social-row">
            <span
              role="button"
              tabIndex={0}
              className={`sup-like-pill${
                activeThread.selfLiked
                  ? " sup-like-pill-active"
                  : ""
              }`}
              onClick={() =>
                likePost(
                  activeThread.id
                )
              }
            >
              ❤ {activeThread.likes}
            </span>

            <span className="sup-comment-pill">
              💬{" "}
              {activeThread.commentCount ||
                0}
            </span>
          </div>

          {isAdmin && (
            <div className="sup-admin-note">
              You're viewing as ADMIN —
              read-only. Only LEAD can reply
              or resolve.
            </div>
          )}

          {isLead && (
            <button
              type="button"
              className="sup-secondary-btn"
              onClick={() =>
                deletePost(
                  activeThread.id
                )
              }
            >
              Delete this thread
            </button>
          )}

          <div className="sup-messages">
            {activeThread.messages?.map(
              (m, i) => (
                <div
                  key={m.id ?? i}
                  className={`sup-message${
                    m.role === "lead"
                      ? " sup-message-lead"
                      : ""
                  }`}
                >
                  <div className="sup-message-head">
                    <span className="sup-message-author">
                      {m.author}
                    </span>

                    <span className="sup-message-time">
                      {formatDate(m.at)}
                    </span>
                  </div>

                  <p>{m.text}</p>

                  {m.isComment && (
                    <span
                      role="button"
                      tabIndex={0}
                      className={`sup-like-pill${
                        m.selfLiked
                          ? " sup-like-pill-active"
                          : ""
                      }`}
                      onClick={() =>
                        likeComment(
                          activeThread.id,
                          m.id
                        )
                      }
                    >
                      ❤ {m.likes || 0}
                    </span>
                  )}
                </div>
              )
            )}
          </div>

          {!isAdmin &&
            activeThread.status ===
              "open" && (
              <form
                className="sup-reply-form"
                onSubmit={sendReply}
              >
                <textarea
                  rows={3}
                  placeholder={
                    isLead
                      ? "Reply as LEAD..."
                      : "Add a reply..."
                  }
                  value={replyText}
                  onChange={(e) =>
                    setReplyText(
                      e.target.value
                    )
                  }
                />

                <div className="sup-reply-actions">
                  {isLead && (
                    <button
                      type="button"
                      className="sup-resolve-btn"
                      onClick={
                        resolveThread
                      }
                    >
                      ✓ Mark resolved
                    </button>
                  )}

                  <button
                    type="submit"
                    className="sup-primary-btn"
                    disabled={
                      sendingReply
                    }
                  >
                    {sendingReply
                      ? "Sending..."
                      : "Send reply"}
                  </button>
                </div>
              </form>
            )}

          {activeThread.status ===
            "resolved" &&
            !isAdmin && (
              <p className="sup-resolved-note">
                ✓ This thread has been
                marked resolved.
              </p>
            )}
        </div>
      )}

      {/* New issue modal */}
      {showNewIssue && (
        <div
          className="sup-modal-backdrop"
          onClick={() =>
            setShowNewIssue(false)
          }
        >
          <form
            className="sup-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
            onSubmit={raiseIssue}
          >
            <h3>Raise an issue</h3>

            <label className="sup-field-label">
              Title
            </label>

            <input
              type="text"
              value={newTitle}
              onChange={(e) =>
                setNewTitle(
                  e.target.value
                )
              }
              placeholder="Short summary of the issue"
              autoFocus
            />

            <label className="sup-field-label">
              Description
            </label>

            <textarea
              rows={4}
              value={newDesc}
              onChange={(e) =>
                setNewDesc(
                  e.target.value
                )
              }
              placeholder="What's going wrong? Include steps to reproduce if you can."
            />

            <label className="sup-field-label">
              Attach image or file
              (optional)
            </label>

            <div className="sup-file-input-row">
              <label className="sup-choose-file-btn">
                Choose file

                <input
                  type="file"
                  className="sup-file-input-hidden"
                  onChange={(e) =>
                    setNewImage(
                      e.target.files?.[0] ||
                        null
                    )
                  }
                />
              </label>

              <span
                className={`sup-file-name${
                  newImage
                    ? ""
                    : " sup-file-name-empty"
                }`}
              >
                {newImage
                  ? newImage.name
                  : "No file chosen"}
              </span>

              {newImage && (
                <button
                  type="button"
                  className="sup-file-clear-btn"
                  onClick={() =>
                    setNewImage(null)
                  }
                  aria-label="Remove file"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="sup-modal-actions">
              <button
                type="button"
                className="sup-secondary-btn"
                onClick={() =>
                  setShowNewIssue(false)
                }
              >
                Cancel
              </button>

              <button
                type="submit"
                className="sup-primary-btn"
                disabled={submitting}
              >
                {submitting
                  ? "Submitting..."
                  : "Submit to LEAD"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}